/**
 * kb-status.ts — Trạng thái nạp / nhúng của mục kiến thức (Tài liệu cho AI) + tiện ích hiển thị.
 * Backend: knowledge/kb-embed-pipeline.ts (embed_status), ingest/ingest-service.ts (source_meta).
 */
import type { BadgeProps } from '@/components/ui/badge'
import { currentLocale, i18n } from '@/lib/i18n'

export type EmbedStatus = 'none' | 'extracting' | 'pending' | 'processing' | 'ready' | 'failed' | 'paused' | 'no_key'

/** Trang Platform nơi quản trị eCDP thêm khoá AI nền tảng (chỉ super-admin đăng nhập được). */
export const PLATFORM_AI_KEYS_PATH = '/platform/ai/keys'

export interface KbSourceMeta {
  kind: 'file' | 'web' | 'gdoc' | 'doc_asset' | 'product_doc'
  fileName?: string | null
  url?: string | null
  finalUrl?: string | null
  size?: number | null
  pages?: number | null
  sheets?: number | null
  rows?: number | null
  chars?: number | null
  truncated?: boolean
  productCode?: string | null
}

export interface KbStatusFields {
  embedStatus?: string | null
  embedError?: string | null
  chunkCount?: number | null
}

export type StatusInfo = {
  label: string
  /** Nhãn ngắn cho huy hiệu (lý do chi tiết hiện ở dòng riêng — `detail`). */
  short: string
  /** Lý do lỗi / tạm dừng — hiện thành dòng chữ dưới tiêu đề. */
  detail?: string
  variant: NonNullable<BadgeProps['variant']>
  /** Đang chạy nền — danh sách tự làm mới. */
  busy: boolean
  /** Hiện nút "Thử lại". */
  canRetry: boolean
  /** Giải thích thêm (tooltip / dòng phụ). */
  hint?: string
}

const BUSY = new Set(['extracting', 'pending', 'processing'])

export function isKbBusy(status?: string | null): boolean {
  return !!status && BUSY.has(status)
}

export function embedStatusInfo(e: KbStatusFields): StatusInfo {
  const n = e.chunkCount ?? 0
  switch (e.embedStatus) {
    case 'ready': {
      const ready = n > 0 ? i18n.t('aiMisc:kbStatus.ready.withChunks', { count: n }) : i18n.t('aiMisc:kbStatus.ready.plain')
      return { label: ready, short: ready, variant: 'success', busy: false, canRetry: false }
    }
    case 'failed': {
      const why = e.embedError || i18n.t('aiMisc:kbStatus.failed.unknownReason')
      return { label: i18n.t('aiMisc:kbStatus.failed.label', { why }), short: i18n.t('aiMisc:kbStatus.failed.short'), detail: why, variant: 'destructive', busy: false, canRetry: true }
    }
    case 'no_key': {
      // Không phải lỗi của tài liệu: hệ thống chưa có khoá AI nền tảng — AI vẫn tra mục này bằng từ khoá.
      const why = e.embedError || i18n.t('aiMisc:kbStatus.noKey.label')
      return {
        label: why, short: i18n.t('aiMisc:kbStatus.noKey.short'), detail: why, variant: 'warning', busy: false, canRetry: true,
        hint: i18n.t('aiMisc:kbStatus.noKey.hint'),
      }
    }
    case 'paused': {
      const hint = i18n.t('aiMisc:kbStatus.paused.hint')
      return {
        label: i18n.t('aiMisc:kbStatus.paused.label'), short: i18n.t('aiMisc:kbStatus.paused.short'), variant: 'warning', busy: false, canRetry: true,
        detail: i18n.t('aiMisc:kbStatus.paused.detail', { hint }), hint: e.embedError || hint,
      }
    }
    case 'extracting':
      return { label: i18n.t('aiMisc:kbStatus.extracting'), short: i18n.t('aiMisc:kbStatus.extracting'), variant: 'secondary', busy: true, canRetry: false }
    case 'pending':
    case 'processing':
      return { label: i18n.t('aiMisc:kbStatus.processing'), short: i18n.t('aiMisc:kbStatus.processing'), variant: 'secondary', busy: true, canRetry: false }
    case 'none':
      return { label: i18n.t('aiMisc:kbStatus.none.label'), short: i18n.t('aiMisc:kbStatus.none.label'), variant: 'outline', busy: false, canRetry: true, hint: i18n.t('aiMisc:kbStatus.none.hint') }
    default:
      return { label: i18n.t('aiMisc:kbStatus.processing'), short: i18n.t('aiMisc:kbStatus.processing'), variant: 'secondary', busy: true, canRetry: false }
  }
}

export function formatBytes(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toLocaleString(currentLocale(), { maximumFractionDigits: 0 })} KB`
  return `${(n / 1024 / 1024).toLocaleString(currentLocale(), { maximumFractionDigits: 1 })} MB`
}

function shortUrl(u: string): string {
  try {
    const x = new URL(u)
    const p = x.pathname === '/' ? '' : x.pathname
    const s = `${x.hostname}${p}`
    return s.length > 60 ? `${s.slice(0, 57)}…` : s
  } catch {
    return u.slice(0, 60)
  }
}

/** Dòng nguồn gốc: "📄 bang-gia.pdf · 12 trang · 1,2 MB" / "🌐 ecdp.vn/gioi-thieu". null nếu nhập tay. */
export function sourceLine(meta?: KbSourceMeta | null): string | null {
  if (!meta || typeof meta !== 'object' || !meta.kind) return null
  const extra = [
    meta.pages ? i18n.t('aiMisc:kbStatus.source.pages', { count: meta.pages }) : null,
    meta.sheets ? i18n.t('aiMisc:kbStatus.source.sheets', { count: meta.sheets }) : null,
    meta.size ? formatBytes(meta.size) : null,
    meta.truncated ? i18n.t('aiMisc:kbStatus.source.truncated') : null,
  ].filter(Boolean)
  const tail = extra.length ? ` · ${extra.join(' · ')}` : ''
  switch (meta.kind) {
    case 'file': return `📄 ${meta.fileName ?? i18n.t('aiMisc:kbStatus.source.defaultFile')}${tail}`
    case 'web': return `🌐 ${meta.url ? shortUrl(meta.url) : i18n.t('aiMisc:kbStatus.source.defaultWeb')}${tail}`
    case 'gdoc': return `📝 ${i18n.t('aiMisc:kbStatus.source.gdoc')}${meta.url ? ` · ${shortUrl(meta.url)}` : ''}`
    case 'doc_asset': return `📚 ${i18n.t('aiMisc:kbStatus.source.docAsset', { name: meta.fileName ?? '' })}${tail}`
    case 'product_doc': return `📦 ${i18n.t('aiMisc:kbStatus.source.productDoc', { code: meta.productCode ?? '' })}`.trim()
    default: return null
  }
}

// ── Tải file ─────────────────────────────────────────────────────────────────

export const KB_ACCEPT = '.pdf,.docx,.xlsx,.csv,.txt,.md,.markdown'
export const KB_MAX_BYTES = 20 * 1024 * 1024

const HINT_KEYS: Record<string, string> = {
  xls: 'xls', doc: 'doc', ppt: 'ppt', pptx: 'ppt', jpg: 'image', jpeg: 'image', png: 'image', webp: 'image',
}

/** Kiểm tra nhanh ở trình duyệt (máy chủ vẫn kiểm lại). null = hợp lệ. */
export function checkKbFile(file: { name: string; size: number }): string | null {
  const ext = (/\.([a-z0-9]+)$/i.exec(file.name)?.[1] ?? '').toLowerCase()
  if (HINT_KEYS[ext]) return i18n.t(`aiMisc:kbStatus.fileHint.${HINT_KEYS[ext]}`)
  if (!KB_ACCEPT.split(',').includes(`.${ext}`)) return i18n.t('aiMisc:kbStatus.file.unsupported')
  if (file.size === 0) return i18n.t('aiMisc:kbStatus.file.empty')
  if (file.size > KB_MAX_BYTES) return i18n.t('aiMisc:kbStatus.file.tooLarge', { size: formatBytes(file.size) })
  return null
}

/** Câu báo thành công sau khi nạp xong. */
export function ingestSuccessMessage(name: string, chunkCount?: number | null): string {
  const n = chunkCount ?? 0
  return n > 0
    ? i18n.t('aiMisc:kbStatus.ingest.withChunks', { count: n, name })
    : i18n.t('aiMisc:kbStatus.ingest.plain', { name })
}

// ── Danh mục mặc định ─────────────────────────────────────────────────

/** Danh mục phổ biến nhất (nhiều mục nhất) — mặc định khi thêm kiến thức từ câu hỏi chưa trả lời được. */
export function mostUsedCategoryId(cats: Array<{ id: string; entryCount?: number | null }> | undefined): string {
  if (!cats?.length) return ''
  return [...cats].sort((a, b) => (b.entryCount ?? 0) - (a.entryCount ?? 0))[0].id
}
